import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Pagination,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from "@mui/material";
import {
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  PictureAsPdf as PdfIcon,
} from "@mui/icons-material";
import { BankStatementUpload } from "./BankStatementUpload";
import { bankStatementService } from "../services/bankStatementService";
import type { BankStatement, BankTransaction } from "../types/bankStatement";

const months = [
  { value: 1, label: "Janeiro" },
  { value: 2, label: "Fevereiro" },
  { value: 3, label: "Março" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Maio" },
  { value: 6, label: "Junho" },
  { value: 7, label: "Julho" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Setembro" },
  { value: 10, label: "Outubro" },
  { value: 11, label: "Novembro" },
  { value: 12, label: "Dezembro" },
];

export const BankStatementsView: React.FC = () => {
  const [statements, setStatements] = useState<BankStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedStatement, setSelectedStatement] = useState<
    (BankStatement & { transactions: BankTransaction[] }) | null
  >(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [statementToDelete, setStatementToDelete] = useState<number | null>(
    null
  );

  // Filters
  const [filterMes, setFilterMes] = useState<number | "">("");
  const [filterAno, setFilterAno] = useState<number>(new Date().getFullYear());
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const loadStatements = async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await bankStatementService.getStatements({
        mes: filterMes === "" ? undefined : filterMes,
        ano: filterAno,
        page,
        limit: 10,
      });

      setStatements(result.statements);
      setTotalPages(result.pagination.pages);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar extratos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStatements();
  }, [page, filterMes, filterAno]);

  const handleViewDetails = async (id: number) => {
    try {
      const statement = await bankStatementService.getStatement(id);
      setSelectedStatement(statement);
      setDetailsOpen(true);
    } catch (err: any) {
      setError(err.message || "Erro ao carregar detalhes do extrato");
    }
  };

  const handleDeleteClick = (id: number) => {
    setStatementToDelete(id);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (statementToDelete) {
      try {
        await bankStatementService.deleteStatement(statementToDelete);
        setDeleteConfirmOpen(false);
        setStatementToDelete(null);
        loadStatements();
      } catch (err: any) {
        setError(err.message || "Erro ao eliminar extrato");
      }
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("pt-PT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString("pt-PT");
  };

  const getMonthName = (mes: number) => {
    return months.find((m) => m.value === mes)?.label || mes.toString();
  };

  const years = Array.from(
    { length: 10 },
    (_, i) => new Date().getFullYear() - i
  );

  return (
    <Box>
      <BankStatementUpload onUploadSuccess={loadStatements} />

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filtros
        </Typography>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel>Ano</InputLabel>
            <Select
              value={filterAno}
              label="Ano"
              onChange={(e: SelectChangeEvent<number>) =>
                setFilterAno(Number(e.target.value))
              }
            >
              {years.map((year) => (
                <MenuItem key={year} value={year}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel>Mês</InputLabel>
            <Select
              value={filterMes}
              label="Mês"
              onChange={(e: SelectChangeEvent<number | "">) =>
                setFilterMes(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
            >
              <MenuItem value="">
                <em>Ano inteiro</em>
              </MenuItem>
              {months.map((month) => (
                <MenuItem key={month.value} value={month.value}>
                  {month.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Período</TableCell>
                  <TableCell>Banco</TableCell>
                  <TableCell>Conta</TableCell>
                  <TableCell align="right">Saldo Inicial</TableCell>
                  <TableCell align="right">Saldo Final</TableCell>
                  <TableCell align="center">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {statements.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        Nenhum extrato encontrado
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  statements.map((statement) => (
                    <TableRow key={statement.id}>
                      <TableCell>
                        <Chip
                          label={`${getMonthName(statement.mes)} ${
                            statement.ano
                          }`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>{statement.banco}</TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 150 }}
                        >
                          {statement.conta || "N/A"}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(statement.saldo_inicial)}
                      </TableCell>
                      <TableCell align="right">
                        {formatCurrency(statement.saldo_final)}
                      </TableCell>
                      <TableCell align="center">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(statement.id)}
                          title="Ver detalhes"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                        {statement.pdf_url && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                `http://localhost:3001${statement.pdf_url}`,
                                "_blank"
                              )
                            }
                            title="Ver PDF"
                          >
                            <PdfIcon fontSize="small" />
                          </IconButton>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClick(statement.id)}
                          title="Eliminar"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box display="flex" justifyContent="center" sx={{ mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Details Dialog */}
      <Dialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Detalhes do Extrato -{" "}
          {selectedStatement &&
            `${getMonthName(selectedStatement.mes)} ${selectedStatement.ano}`}
        </DialogTitle>
        <DialogContent>
          {selectedStatement && (
            <Box>
              <Stack spacing={2} sx={{ mb: 3 }}>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Banco
                  </Typography>
                  <Typography variant="body1">
                    {selectedStatement.banco}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Conta
                  </Typography>
                  <Typography variant="body1">
                    {selectedStatement.conta || "N/A"}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={4}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Saldo Inicial
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(selectedStatement.saldo_inicial)}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Saldo Final
                    </Typography>
                    <Typography variant="body1">
                      {formatCurrency(selectedStatement.saldo_final)}
                    </Typography>
                  </Box>
                </Stack>
              </Stack>

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Movimentos ({selectedStatement.transactions.length})
              </Typography>

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Descrição</TableCell>
                      <TableCell align="right">Valor</TableCell>
                      <TableCell align="right">Saldo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedStatement.transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          {formatDate(transaction.data_movimento)}
                        </TableCell>
                        <TableCell>{transaction.descricao}</TableCell>
                        <TableCell
                          align="right"
                          sx={{
                            color:
                              transaction.valor >= 0
                                ? "success.main"
                                : "error.main",
                          }}
                        >
                          {formatCurrency(transaction.valor)}
                        </TableCell>
                        <TableCell align="right">
                          {formatCurrency(transaction.saldo)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
      >
        <DialogTitle>Confirmar Eliminação</DialogTitle>
        <DialogContent>
          <Typography>
            Tem a certeza que deseja eliminar este extrato? Esta ação não pode
            ser revertida.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
